import React, { useContext } from "react";
import Instructions from "../instructions/Instructions";
import { InterviewSession } from "../../../../Interface/Index";
import QuestionSectionHeader from "./QuestionSectionHeader";
import Question from "./Question";
import Evaluate from "../Evaluate";
import { DashboardContext } from "../../../../Context/DashboardContext";
import QuestionRenderer from "../QuestionRenderer";
import { Empty, RadioChangeEvent } from "antd";
import DisqualifiedMessage from "../DisqualifiedMessage";
import usePing from "../../hooks/usePing";

interface InterviewRoundContentProps {
  activeQuestionIndex: number;
  currentSession: InterviewSession;
  currentSessionQuestions: any[];
  handleStartInterview: () => void;
  handleNext: () => void;
  handleBack: () => void;
  isRoundFinished: boolean;
  currentSessionQuestionAnswers: any[];
  handleEvaluation: () => void;
  onNavigateToQuestions: () => void;
  handleQuestionAnswerChange: (
    e: React.ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent,
    value?: string | null
  ) => void;
  containerRef: React.RefObject<HTMLDivElement> | null;
  sessionQuestions: any[];
  isSameDevice: boolean;
  terminatedStats: {
    isLimitReached: boolean;
    violationType: string;
  };
}

const InterviewRoundContent: React.FC<InterviewRoundContentProps> = ({
  activeQuestionIndex,
  currentSession,
  currentSessionQuestions,
  handleStartInterview,
  handleNext,
  handleBack,
  isRoundFinished,
  currentSessionQuestionAnswers,
  handleEvaluation,
  onNavigateToQuestions,
  handleQuestionAnswerChange,
  containerRef,
  sessionQuestions,
  isSameDevice,
  terminatedStats,
}) => {
  const { checkLastRound, appliedJob } = useContext(DashboardContext);

  // HeartBeatMonitoring
  usePing(currentSession, isRoundFinished, activeQuestionIndex);

  // Render instructions when question index is -1
  if (activeQuestionIndex === -1) {
    return (
      <div className="instruction-section">
        <DisqualifiedMessage
          isSameDevice={isSameDevice}
          terminatedStats={terminatedStats}
        />
        {isSameDevice && !terminatedStats.isLimitReached && (
          <Instructions
            session={currentSession}
            handleStart={handleStartInterview}
          />
        )}
      </div>
    );
  }

  // Render Questions when question index is not -1 and is less than the total number of questions
  if (
    activeQuestionIndex >= 0 &&
    activeQuestionIndex < currentSessionQuestions.length &&
    sessionQuestions.length > 0
  ) {
    return (
      <div
        className="question-section"
        style={
          currentSessionQuestions[activeQuestionIndex] &&
          currentSessionQuestions[activeQuestionIndex].type === "coding"
            ? { width: "100%" }
            : { maxWidth: "673px" }
        }
      >
        <QuestionSectionHeader
          activeQuestionIndex={activeQuestionIndex}
          currentSessionQuestions={currentSessionQuestions}
          handleBack={handleBack}
          handleNext={handleNext}
        />

        <Question
          currentQuestion={currentSessionQuestions[activeQuestionIndex]}
          containerRef={containerRef}
        />
        <div className="question-type">
          <QuestionRenderer
            question={currentSessionQuestions[activeQuestionIndex]}
            questionAnswer={currentSessionQuestionAnswers[activeQuestionIndex]}
            handleQuestionAnswer={handleQuestionAnswerChange}
            isRoundFinished={isRoundFinished}
          />
        </div>
      </div>
    );
  }

  // If activeQuestionIndex is greater than or equal to the length of currentSessionQuestions render last step
  if (
    activeQuestionIndex >= currentSessionQuestions.length &&
    appliedJob
  ) {
    return (
      <Evaluate
        isRoundFinished={isRoundFinished}
        questionRoundAnswers={currentSessionQuestionAnswers}
        currentInterviewSession={currentSession}
        questions={currentSessionQuestions}
        handleEvaluation={handleEvaluation}
        appliedJob={appliedJob}
        onNavigateToQuestions={onNavigateToQuestions}
        isLastRound={checkLastRound(currentSession._id)}
      />
    );
  }

  return <Empty description="No questions available." />;
};

export default InterviewRoundContent;
